import { Divider, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import type { CustomerOverview } from '@weft/api-definition';
import { isNotNullOrUndefined, validateEnum, withDefault } from '@weft/utils';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { AutocompleteSelect } from '../../../../../../components/design/AutoCompleteSelect';
import { Spinner } from '../../../../../../components/design/Spinner';
import { withSuspenseBoundary } from '../../../../../../components/utility/SuspenseBoundary';
import { useCustomersOverviews } from '../../../../../../hooks/requests/use-customers-overviews/use-customers-overviews';
import { ContractDatePicker } from '../../../../PlansPage/plans-page-components/ContractDatePicker';
import { PLAN_SAVING_OPTIONS } from '../save-and-assign-as-contract-dialog-dialog-form-types/plan-saving-option';
import type { SaveAndAssignAsContractForm } from '../SaveAndAssignAsContractDialogForm';

const SaveAndAssignAsContractDialogFormInputs: React.FC = () => {
  const { data, isLoading: isLoadingCustomers } = useCustomersOverviews();
  const { watch, control, setValue, resetField } = useFormContext<SaveAndAssignAsContractForm>();

  const customersWithNoActiveContracts = getCustomersWithNoExistingContracts(withDefault(data?.customers, []));

  const selectedCustomer = useSelectedCustomer(customersWithNoActiveContracts);
  const currentPlanSavingOption = watch('planSavingOption');

  const handleCustomerSelect = (_event: React.SyntheticEvent<Element, Event>, newSelectedCustomer: CustomerOverview | null) => {
    if (isNotNullOrUndefined(newSelectedCustomer)) {
      setValue(`${PLAN_SAVING_OPTIONS.AssignCustomer}.customerId`, newSelectedCustomer.id);
    } else {
      setValue(`${PLAN_SAVING_OPTIONS.AssignCustomer}.customerId`, '');
    }
  };

  return (
    <RadioGroup
      className="mb-14 mt-6 flex w-full flex-col gap-y-4"
      value={currentPlanSavingOption}
      onChange={(_event, newOption) => {
        validateEnum(newOption, PLAN_SAVING_OPTIONS);
        setValue('planSavingOption', newOption);
        resetField(PLAN_SAVING_OPTIONS.AssignCustomer);
      }}
    >
      <FormControlLabel
        value={PLAN_SAVING_OPTIONS.AssignCustomer}
        control={<Radio />}
        label="Assign and save as a contract"
        className="m-0 w-full rounded-xl border border-[#DFEBF1] p-3"
      />

      {
        currentPlanSavingOption === PLAN_SAVING_OPTIONS.AssignCustomer && (
          <Controller
            control={control}
            name={`${PLAN_SAVING_OPTIONS.AssignCustomer}.customerId`}
            render={() => (
              <AutocompleteSelect
                loading={isLoadingCustomers}
                options={customersWithNoActiveContracts}
                loadingText={<Spinner/>}
                getOptionLabel={(option) => option.name}
                textFieldProps={{
                  focused: true,
                  placeholder: 'Search for a customer',
                }}
                onChange={handleCustomerSelect}
              />
            )}
          />
        )
      }

      {
        isNotNullOrUndefined(selectedCustomer) && (
          <div className="flex flex-col gap-y-3 rounded-xl border border-[#DFEBF1] p-5">
            <span className="text-lg font-light">{selectedCustomer.name}</span>
            <span className="text-base font-light text-[#576063]">Define a starting date for this plan</span>

            <ContractDatePicker
              date={watch(`${PLAN_SAVING_OPTIONS.AssignCustomer}.startDate`)}
              onSelect={(newDate) => {
                if (isNotNullOrUndefined(newDate)) {
                  setValue(`${PLAN_SAVING_OPTIONS.AssignCustomer}.startDate`, newDate);
                }
              }}
              containerClassName="w-full"
            />
          </div>
        )
      }

      <div className="flex w-full flex-row items-center gap-x-4">
        <Divider className="flex flex-1"/>
        <div className="flex">or</div>
        <Divider className="flex flex-1"/>
      </div>

      <FormControlLabel
        value={PLAN_SAVING_OPTIONS.Template}
        control={<Radio />}
        label="Save as a template"
        className="m-0 w-full rounded-xl border border-[#DFEBF1] p-3"
      />
    </RadioGroup>
  );
};

function useSelectedCustomer(customers: CustomerOverview[]) {
  const { watch } = useFormContext<SaveAndAssignAsContractForm>();
  const selectedCustomerId = watch(`${PLAN_SAVING_OPTIONS.AssignCustomer}.customerId`);

  return customers.find((customer) => customer.id === selectedCustomerId);
}

function getCustomersWithNoExistingContracts(customers: CustomerOverview[]) {
  return customers.filter((customer) => customer.activeContractsCount === 0);
}

const SaveAndAssignAsContractDialogFormInputsWithSuspenseBoundary = withSuspenseBoundary(SaveAndAssignAsContractDialogFormInputs);
export { SaveAndAssignAsContractDialogFormInputsWithSuspenseBoundary as SaveAndAssignAsContractDialogFormInputs };